import { useState, useCallback, useMemo } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Spinner } from 'common/Spinner/Spinner'
import Styles from './RegenerateDesignModal.module.scss'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import {
  ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
  ACCEPTED_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useShowCreditsPaywall } from 'hooks/useShowCreditsPaywall'
import { RefineSettings } from './components/RefineSettings'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import type { Provider } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { usePerformRegeneration } from './hooks/usePerformRegeneration'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useBrandKitAvatars } from 'hooks/queries/brandKits/useBrandKitAvatars'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/useAISuggestionRequest'
import { avatarParamsFromSettings, avatarSettingsFromPrompt } from 'utils/avatarScriptSettings'
import {
  getVideoScriptRequestParamsFromAlmanacFile,
  isUgcScriptVideoContent,
  mergeVideoScriptPrompts,
  videoParamsFromSettings,
  videoScriptSettingsFromPrompt,
} from 'utils/videoScriptSettings'
import {
  AvatarScriptSettings,
  ScriptSettingsModal,
} from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/NewPostModal/ScriptSettingsModal'

export type MediaSourceOverride = 'ai_video' | 'ai_image' | 'default'

const getMediaSourceOverride = (provider: string | undefined): MediaSourceOverride => {
  if (provider === 'veo3') return 'ai_video'
  if (provider === 'ai_generated') return 'ai_image'
  return 'default'
}

interface RegenerateDesignModalProps {
  almanacFile: AlmanacFileShowItem
}

const HIDDEN_PROVIDERS_DEFAULT: Provider[] = ['gifs', 'blazeRecommendations', 'stockVideo']
const HIDDEN_PROVIDERS_AI_GENERATED: Provider[] = ['stockVideo', 'stockPhoto', 'gifs', 'blazeRecommendations']
const HIDDEN_PROVIDERS_VIDEO: Provider[] = ['gifs', 'blazeRecommendations', 'stockPhoto', 'ai']

export const RegenerateDesignModal = ({ almanacFile }: RegenerateDesignModalProps) => {
  const { closeModal, openModal } = useModals()
  const { usageCosts, creditsRemaining, canAutoReload } = useCreditUsage()
  const { price, isTrackingCredits } = useShowCreditsPaywall()
  const sourceMediaInfo = almanacFile.source_media_info

  const [topicOverride, setTopicOverride] = useState<string | null>(null)
  const [sourceMediaOverride, setSourceMediaOverride] = useState<ImageMenuSelectedImage | null>(null)
  const [error, setError] = useState<string | null>(null)

  const topic = topicOverride ?? sourceMediaInfo?.topic ?? ''
  const displaySourceMediaUrl = sourceMediaOverride?.url ?? sourceMediaInfo?.source_url ?? ''

  const isVideoAsset = almanacFile.asset_type === 'Video'
  const isAvatarVideo = almanacFile.content_type === 'avatar_video'
  const showVideoVoiceoverCaptions = useFlipperFlag('video_voiceover_captions')
  const isUgcScriptVideo = showVideoVoiceoverCaptions && isUgcScriptVideoContent(almanacFile)
  const completedPrompt = almanacFile.completed_ai_suggestion_request?.prompt
  const brandKitId = typeof completedPrompt?.brand_kit_id === 'number' ? completedPrompt.brand_kit_id : undefined
  const avatarBrandKitId = isAvatarVideo ? brandKitId : undefined
  const { data: brandKitAvatars } = useBrandKitAvatars(
    { brandKitId: avatarBrandKitId },
    { enabled: isAvatarVideo && !!avatarBrandKitId },
  )
  // avatar_config reflects the latest generation; completedPrompt goes stale after a regeneration.
  const avatarSettings = avatarSettingsFromPrompt(almanacFile.avatar_config ?? completedPrompt, brandKitAvatars ?? [])
  const completedRequestId = almanacFile.completed_ai_suggestion_request?.id
  const { data: suggestionRequestData, isLoading: isLoadingSuggestionRequest } = useAISuggestionRequest(
    { id: completedRequestId ?? 0 },
    { enabled: isUgcScriptVideo && !!completedRequestId },
  )
  const videoSettings = useMemo(() => {
    // video_script reflects the latest generation; completedPrompt goes stale after a regeneration.
    const merged = mergeVideoScriptPrompts(
      completedPrompt,
      suggestionRequestData?.request?.prompt as Record<string, unknown> | undefined,
    )
    return videoScriptSettingsFromPrompt(
      almanacFile.video_script != null ? { ...merged, video_script: almanacFile.video_script } : merged,
    )
  }, [almanacFile.video_script, completedPrompt, suggestionRequestData?.request?.prompt])
  const topicSeed = (typeof completedPrompt?.topic === 'string' && completedPrompt.topic) || topic
  const videoScriptRequest = useMemo(
    () => getVideoScriptRequestParamsFromAlmanacFile(almanacFile, topicSeed),
    [almanacFile, topicSeed],
  )

  const isAiGenerated =
    isAvatarVideo ||
    isUgcScriptVideo ||
    sourceMediaInfo?.provider === 'ai_generated' ||
    sourceMediaInfo?.provider === 'veo3'
  const isSourceVideo = sourceMediaInfo?.mime_type?.startsWith('video/')

  const { performRegeneration, isLoading } = usePerformRegeneration({
    fileId: almanacFile.id,
    assetType: almanacFile.asset_type,
    isAiGenerated,
    onSuccess: closeModal,
    onError: setError,
  })

  const imageMenuHiddenProviders = useMemo((): Provider[] => {
    if (isAiGenerated) {
      return HIDDEN_PROVIDERS_AI_GENERATED
    }
    if (isSourceVideo) {
      return HIDDEN_PROVIDERS_VIDEO
    }
    return HIDDEN_PROVIDERS_DEFAULT
  }, [isAiGenerated, isSourceVideo])

  const generationCost = useMemo(() => {
    if (isAvatarVideo) return usageCosts?.avatar_video_credits_cost
    if (isUgcScriptVideo || isVideoAsset) return usageCosts?.video_credits_cost
    switch (sourceMediaInfo?.provider) {
      case 'veo3':
        return usageCosts?.video_credits_cost
      case 'ai_generated':
        return usageCosts?.visual_styles_credits_cost
      default:
        return isTrackingCredits ? usageCosts?.content_credits_cost : 0
    }
  }, [isAvatarVideo, isUgcScriptVideo, isVideoAsset, sourceMediaInfo?.provider, usageCosts, isTrackingCredits])

  const hasInsufficientCredits = creditsRemaining < (generationCost ?? 0) && !!price && !canAutoReload

  const handlePerformRegeneration = useCallback(() => {
    const mediaFileKey = sourceMediaOverride?.id ?? sourceMediaInfo?.key ?? null
    const mediaSourceOverride = getMediaSourceOverride(sourceMediaInfo?.provider)
    setError(null)
    performRegeneration({ mediaFileKey, topic, mediaSourceOverride })
  }, [sourceMediaOverride, sourceMediaInfo, topic, performRegeneration])

  const handleAvatarRegenerate = useCallback(
    (settings: AvatarScriptSettings, topicValue: string) => {
      const run = () => {
        setError(null)
        performRegeneration({
          mediaFileKey: settings.productImage?.key || null,
          topic: topicValue,
          mediaSourceOverride: 'default',
          avatarParams: avatarParamsFromSettings(settings),
        })
      }
      if (hasInsufficientCredits) {
        openModal(
          PurchaseContentCreditsModal,
          { price, onSuccess: run, defaultBatchCount: Math.ceil((generationCost ?? 0) / 100) || 1 },
          { newStack: true },
        )
        return
      }
      run()
    },
    [performRegeneration, hasInsufficientCredits, price, generationCost, openModal],
  )

  const handleVideoRegenerate = useCallback(
    (settings: AvatarScriptSettings, topicValue: string) => {
      const run = () => {
        setError(null)
        performRegeneration({
          mediaFileKey: sourceMediaInfo?.key ?? null,
          topic: topicValue,
          mediaSourceOverride: getMediaSourceOverride(sourceMediaInfo?.provider),
          videoParams: videoParamsFromSettings(settings),
        })
      }
      if (hasInsufficientCredits) {
        openModal(
          PurchaseContentCreditsModal,
          { price, onSuccess: run, defaultBatchCount: Math.ceil((generationCost ?? 0) / 100) || 1 },
          { newStack: true },
        )
        return
      }
      run()
    },
    [
      performRegeneration,
      hasInsufficientCredits,
      price,
      generationCost,
      openModal,
      sourceMediaInfo?.key,
      sourceMediaInfo?.provider,
    ],
  )

  const handleRegenerate = useCallback(() => {
    if (hasInsufficientCredits) {
      openModal(
        PurchaseContentCreditsModal,
        {
          price,
          onSuccess: handlePerformRegeneration,
          defaultBatchCount: Math.ceil((generationCost ?? 0) / 100) || 1,
        },
        { newStack: true },
      )
      return
    }

    handlePerformRegeneration()
  }, [hasInsufficientCredits, generationCost, price, openModal, handlePerformRegeneration])

  const handleReplaceImage = useCallback(() => {
    const accept = isSourceVideo && !isAiGenerated ? ACCEPTED_VIDEO_MIME_TYPES : ACCEPTED_IMAGE_MIME_TYPES_NO_SVG
    openModal(
      ImageMenuModal,
      {
        accept,
        hiddenProviders: imageMenuHiddenProviders,
        maxCount: 1,
        uploadMedia: true,
        title: 'Select Source',
        onUse: ([image]: ImageMenuSelectedImage[]) => {
          if (image) {
            setSourceMediaOverride(image)
          }
        },
      },
      { newStack: true },
    )
  }, [openModal, imageMenuHiddenProviders, isSourceVideo, isAiGenerated])

  if (isAvatarVideo && avatarBrandKitId) {
    if (!brandKitAvatars) {
      return (
        <Modal.Root size="lg" blaze onPressOutside={() => {}} aria-label="Regenerate video">
          <Modal.Header>
            <Heading level={2}>Regenerate AI Avatar Video</Heading>
          </Modal.Header>
          <Modal.Content>
            <div className={Styles.avatarLoading}>
              <Spinner variant="dark" />
            </div>
          </Modal.Content>
        </Modal.Root>
      )
    }

    let submitLabel = 'Regenerate AI Avatar Video'
    if (hasInsufficientCredits) submitLabel = 'Add credits'
    else if (isLoading) submitLabel = 'Regenerating video...'

    return (
      <ScriptSettingsModal
        value={avatarSettings}
        topic={topicSeed}
        brandKitId={avatarBrandKitId}
        autoGenerate={false}
        heading="Regenerate AI Avatar Video"
        onSubmit={handleAvatarRegenerate}
        submitLabel={submitLabel}
        generationCost={generationCost ?? 0}
        isSubmitting={isLoading}
      />
    )
  }

  if (isUgcScriptVideo && brandKitId) {
    if (completedRequestId && isLoadingSuggestionRequest) {
      return (
        <Modal.Root size="lg" blaze onPressOutside={() => {}} aria-label="Regenerate video">
          <Modal.Header>
            <Heading level={2}>Regenerate video</Heading>
          </Modal.Header>
          <Modal.Content>
            <div className={Styles.avatarLoading}>
              <Spinner variant="dark" />
            </div>
          </Modal.Content>
        </Modal.Root>
      )
    }

    let submitLabel = 'Regenerate video'
    if (hasInsufficientCredits) submitLabel = 'Add credits'
    else if (isLoading) submitLabel = 'Regenerating video...'

    return (
      <ScriptSettingsModal
        mode="ugc_video"
        value={videoSettings}
        topic={topicSeed}
        brandKitId={brandKitId}
        brandPlanScheduleId={almanacFile.campaign_info?.id}
        videoScriptRequest={videoScriptRequest}
        autoGenerate={false}
        heading="Regenerate video"
        onSubmit={handleVideoRegenerate}
        submitLabel={submitLabel}
        generationCost={generationCost ?? 0}
        isSubmitting={isLoading}
      />
    )
  }

  return (
    <Modal.Root size="lg" onPressOutside={() => {}}>
      <RefineSettings
        topic={topic}
        setTopic={setTopicOverride}
        sourceMediaUrl={displaySourceMediaUrl}
        error={error}
        generationCost={generationCost ?? 0}
        isLoading={isLoading}
        onClose={closeModal}
        onRegenerate={handleRegenerate}
        onReplaceImage={handleReplaceImage}
        provider={sourceMediaInfo?.provider}
        hasInsufficientCredits={hasInsufficientCredits}
      />
    </Modal.Root>
  )
}
